import { StandardTextfield, useFetch, UseFetchOptions } from '@brandfolder/react';
import { slugify } from '@brandfolder/utilities';
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from 'next/router';
import { FunctionComponent, HTMLAttributes, useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import styled from 'styled-components';
import { object, SchemaOf } from 'yup';

import { FormFooter } from '@components/forms';
import { BrandguideContext } from '@context/brandguide';
import { LocaleContext } from '@context/locale';
import { PageContext } from '@context/page';
import { PagesContext } from '@context/pages';
import { keySchema, positionSchema } from '@schemas/document';
import { localeSchema } from '@schemas/locale';
import { pageNameSchema, pageSlugSchema } from '@schemas/page';
import { ApiDatum } from '@typings/api';
import { Page, PagePatch, PagePost } from '@typings/page';
import { standardError } from '@utilities/messages';
import { insertPage, updatePage } from '@utilities/page';

interface PagesAddEditPageProps extends HTMLAttributes<HTMLFormElement> {}

const UnstyledPagesAddEditPage: FunctionComponent<PagesAddEditPageProps> = (props) => {
  const { asPath, replace } = useRouter();

  const { brandguide } = useContext(BrandguideContext);
  const { locale } = useContext(LocaleContext);
  const { page, pages, setPage, setPages } = useContext(PageContext);
  const { editingPage, setEditingPage } = useContext(PagesContext);

  const pagePostUserInputSchema: SchemaOf<PagePost> = object({
    brandguideKey: keySchema.required(),
    locale: localeSchema.required(),
    name: pageNameSchema.required('Name is required'),
    slug: pageSlugSchema
      .test('unique', 'Slugs must be unique', (value) => {
        if (!value) return true;

        return !pages.some((p) => p.key !== editingPage?.key && p.slug === value);
      })
      .required('Slug is required'),
    position: positionSchema.required()
  });

  const postPageFetchOptions: UseFetchOptions = {
    fetchOnMount: false,
    headers: {
      'Content-Type': 'application/json',
      'x-brandguide-key': brandguide?.key || ''
    },
    method: 'POST',
    url: `/api/pages`
  };

  const {
    error: postError,
    fetch: postFetch,
    loading: postLoading,
    response: postResponse
  } = useFetch<ApiDatum<Page>>(postPageFetchOptions);

  const patchPageFetchOptions: UseFetchOptions = {
    fetchOnMount: false,
    headers: {
      'Content-Type': 'application/json',
      'x-brandguide-key': brandguide?.key || ''
    },
    method: 'PATCH',
    url: `/api/pages/${editingPage?.key}`
  };

  const {
    error: patchError,
    fetch: patchFetch,
    loading: patchLoading,
    response: patchResponse
  } = useFetch<ApiDatum<Page>>(patchPageFetchOptions);

  const defaultValues = {
    brandguideKey: brandguide?.key,
    locale,
    name: editingPage?.name,
    position: editingPage?.key ? editingPage.position : pages.length,
    slug: editingPage?.slug
  };

  const {
    formState: { errors, isDirty, touchedFields },
    getValues,
    handleSubmit,
    register,
    setValue
  } = useForm<PagePost>({
    defaultValues,
    resolver: yupResolver(pagePostUserInputSchema)
  });

  const { onChange: nameOnChange, ...nameInputProps } = register('name');
  const { onBlur: slugOnBlur, ...slugInputProps } = register('slug');

  useEffect(() => {
    if (postResponse) {
      setPages([
        ...insertPage({ ...postResponse.data.attributes, key: postResponse.data.id }, pages)
      ]);
      setEditingPage(null);
    }
  }, [postResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateUrl = async (newPageSlug: string) => {
    // removes /brandguide-slug/old-page-slug and changes to /brandguide-slug/new-page-slug
    const url = `${asPath.split('/').slice(0, -1).join('/')}/${newPageSlug}`;
    // change the address bar url without doing anything
    await replace(url, undefined, { shallow: true });
  };

  useEffect(() => {
    if (patchResponse) {
      // if we just edited the current page
      if (page && page.key === patchResponse.data.id) {
        // update the page in context
        setPage({ ...page, ...patchResponse.data.attributes });

        // if the url address bar includes the page slug
        if (page.slug !== patchResponse.data.attributes.slug && asPath.includes(page.slug)) {
          updateUrl(patchResponse.data.attributes.slug);
        }
      }

      setPages([...updatePage({ ...page, ...patchResponse.data.attributes }, pages)]);
      setEditingPage(null);
    }
  }, [patchResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (patchError || postError) {
      toast.error(standardError);
    }
  }, [patchError, postError]);

  if (!brandguide) return null;

  return (
    <form
      {...props}
      noValidate
      onSubmit={handleSubmit((values: PagePost) => {
        if (editingPage?.key) {
          patchFetch({
            ...patchPageFetchOptions,
            body: {
              data: {
                attributes: {
                  name: values.name,
                  slug: values.slug
                } satisfies PagePatch
              }
            }
          });
        } else {
          postFetch({
            ...postPageFetchOptions,
            body: {
              data: {
                attributes: values
              }
            }
          });
        }
      })}
    >
      <div className="create-page-form__body">
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-expect-error */}
        <StandardTextfield
          {...nameInputProps}
          disabled={postLoading || patchLoading}
          error={errors.name?.message}
          label="Page name"
          id="create-page-name"
          onChange={(e): void => {
            nameOnChange(e);
            if (!touchedFields.slug && !defaultValues.slug) {
              setValue('slug', slugify(e.currentTarget.value));
            }
          }}
          required
          requiredLabel="Required"
        />
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-expect-error */}
        <StandardTextfield
          {...slugInputProps}
          before="/"
          disabled={postLoading || patchLoading}
          error={errors.slug?.message}
          id="create-page-slug"
          label="Slug"
          onBlur={(e): void => {
            slugOnBlur(e);
            setValue('slug', slugify(getValues('slug')));
          }}
          required
          requiredLabel="Required"
        />
      </div>
      <FormFooter
        isDirty={isDirty}
        loading={postLoading || patchLoading}
        onCancel={() => {
          setEditingPage(null);
        }}
        saveButtonText={editingPage?.key ? 'Save page' : 'Save and create page'}
      />
    </form>
  );
};

const StyledPagesAddEditPage = styled(UnstyledPagesAddEditPage)`
  .create-page-form__body {
    padding: 20px 20px 28px;
  }

  .bf-textfield__wrapper {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

export const PagesAddEditPage: FunctionComponent<HTMLAttributes<HTMLFormElement>> = (props) => {
  return <StyledPagesAddEditPage {...props} />;
};
